import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { APP_MENU_ROUTES, IAppMenuRoute } from '../../constants/menu-routes';
import { IHeaderSummary } from 'src/app/models/generic';
import { DataService, ISubheaderOverride } from 'src/app/services/data.service';
import { StatusBadgeTone } from '../../../shared/components/status-badge/status-badge.component';

@Component({
  selector: 'app-global-subheader',
  templateUrl: './global-subheader.component.html',
  styleUrls: ['./global-subheader.component.scss']
})
export class GlobalSubheaderComponent implements OnInit, OnDestroy {
  pageTitle = '';
  autoShowBackButton = false;
  autoBackLink = '';
  kpis: IHeaderSummary[] = [];
  titleOverride: ISubheaderOverride | null = null;
  private routeSubscription?: Subscription;
  private summarySubscription?: Subscription;
  private overrideSubscription?: Subscription;

  constructor(private readonly router: Router, private readonly _dataService: DataService) {}

  ngOnInit(): void {
    this.updateTitleByRoute(this.router.url);

    this.routeSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.updateTitleByRoute(event.urlAfterRedirects);
      });

    this.summarySubscription = this._dataService.headerSummary.subscribe({
      next: (summary) => (this.kpis = summary)
    });

    this.overrideSubscription = this._dataService.subheaderOverride.subscribe({
      next: (override) => (this.titleOverride = override)
    });
  }

  ngOnDestroy(): void {
    this.routeSubscription?.unsubscribe();
    this.summarySubscription?.unsubscribe();
    this.overrideSubscription?.unsubscribe();
  }

  get displayTitle(): string {
    return this.titleOverride?.title || this.pageTitle;
  }

  // Breadcrumb com a tela de origem (ex.: "Esteira de Apuração › Contrato 123"):
  // resolvido a partir do backLink do override, casado contra APP_MENU_ROUTES —
  // não precisa ser hardcoded em cada tela que define um subheaderOverride.
  get breadcrumbSource(): string {
    const backLink = this.titleOverride?.backLink;

    if (!backLink || this.titleOverride?.plainTitle) {
      return '';
    }

    const sanitized = backLink.split('?')[0].split('#')[0];

    const match = APP_MENU_ROUTES
      .filter((route) => route.showInSubheader !== false)
      .sort((a: IAppMenuRoute, b: IAppMenuRoute) => b.link.length - a.link.length)
      .find((route) => sanitized === route.link || sanitized.startsWith(route.link + '/'));

    return match?.name || '';
  }

  get badgeTone(): StatusBadgeTone {
    return this.titleOverride?.badgeTone || 'neutral';
  }

  get showBackButton(): boolean {
    return !!this.titleOverride?.backLink || this.autoShowBackButton;
  }

  get backLink(): string {
    return this.titleOverride?.backLink || this.autoBackLink;
  }

  private updateTitleByRoute(url: string): void {
    const sanitizedUrl = url.split('?')[0].split('#')[0];

    const activeRoute = APP_MENU_ROUTES
      .filter((route) => route.showInSubheader !== false)
      .sort((a: IAppMenuRoute, b: IAppMenuRoute) => b.link.length - a.link.length)
      .find((route) => sanitizedUrl === route.link || sanitizedUrl.startsWith(route.link + '/'));

    this.pageTitle = activeRoute?.name || '';
    this.kpis = [];
    this._dataService.setSubheaderOverride(null);
    this.updateBackButtonState(sanitizedUrl);
  }

  goBack(): void {
    if (!this.showBackButton || !this.backLink) {
      return;
    }

    this.router.navigate([this.backLink]);
  }

  private updateBackButtonState(sanitizedUrl: string): void {
    const segments = sanitizedUrl.split('/').filter(Boolean);
    const hasDetailsSegment = segments.slice(1).some((segment) =>
      segment.toLowerCase().startsWith('detalhes')
    );

    if (segments.length > 0 && hasDetailsSegment) {
      this.autoShowBackButton = true;
      this.autoBackLink = `/${segments[0]}`;
      return;
    }

    this.autoShowBackButton = false;
    this.autoBackLink = '';
  }
}
